import { Button, Center, Container, Group, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { useDisclosure, useInterval } from '@mantine/hooks';
import { IconCheck } from '@tabler/icons-react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken, safeDecodeURIComponent } from '@mygpt/app';
import MailVerificationModal from '@/components/LoginCallback/MailVerificationModal';
import { useLogin } from '@/hooks/useLogin';
import { TokenInfo } from '@/types/interface';
import { getUrlHashParams } from '@/utils/helper';
import { saveTokenInfo } from '@/utils/tool';

export default () => {
  const { t } = useTranslation();

  const [text, setText] = useState('');
  const [opened, { close, open }] = useDisclosure(false);
  const { login } = useLogin();

  // 按钮数秒
  const MAX_SEND_INTERVAL = 60;
  const [emailVerificationSeconds, setEmailVerificationSeconds] = useState(MAX_SEND_INTERVAL);
  const emailVerificationInterval = useInterval(() => setEmailVerificationSeconds((s) => s - 1), 1000);
  const isEmailVerification = useMemo(() => {
    return emailVerificationSeconds < MAX_SEND_INTERVAL;
  }, [emailVerificationSeconds]);
  useEffect(() => {
    if (emailVerificationSeconds <= 1) {
      emailVerificationInterval.stop();
      setEmailVerificationSeconds(MAX_SEND_INTERVAL);
    }
  }, [emailVerificationSeconds]);

  const isEmailVerify = useMemo(() => {
    return text.indexOf('Please verify your email before logging in') !== -1;
  }, [text]);

  const isEmailVerificationSuccess = useMemo(() => {
    return text.indexOf('Your email was verified') !== -1;
  }, [text]);

  const initUrlToken = async () => {
    const urlParams = getUrlHashParams();

    let { access_token, expires_in, refresh_token, token_type } = urlParams;
    const { code, error, error_description, login_type, message, state } = urlParams;

    /**
     * 第一版 url回调urltoken 直接登录
     * 第二版 url回调code 请求获取token和refresh token
     */

    if (code) {
      const res = await getToken({ code });
      const data: TokenInfo = res.data;
      access_token = data.access_token;
      expires_in = data.expires_in;
      refresh_token = data.refresh_token;
      token_type = data.token_type;
    }

    saveTokenInfo({ access_token, refresh_token, expires_in, token_type, scope: '' });

    return new Promise((resolve, reject) => {
      /** 其他 */

      if (login_type === 'email_verify') {
        reject({ errorDescription: message || 'Your email was verified' });
        return;
      }

      if (error && error_description) {
        reject({ error, errorDescription: error_description });
        // 有错误就不跳转了
        return;
      } else if (access_token && token_type && expires_in) {
        resolve('');
      }

      // state参数  目前存了redirect url地址
      if (state && state !== 'None') {
        const redirectUrl = atob(safeDecodeURIComponent(state));
        window.location.href = redirectUrl;
      }
    });
  };

  useLayoutEffect(() => {
    initUrlToken().catch(({ error, errorDescription }) => {
      setText(errorDescription || '');
    });
  }, []);

  return (
    <>
      <Container>
        <Center className="pt-20">
          {isEmailVerificationSuccess ? (
            <Stack align="center" gap={30}>
              <ThemeIcon color="teal.6" radius={65} size={65}>
                <IconCheck size={40} />
              </ThemeIcon>
              <Button
                onClick={() => {
                  // 检查是否有保存的路径，如果有则传入login函数，否则使用默认路径
                  const savedPath = localStorage.getItem('MY_GPT_PATH_NAME');
                  if (savedPath) {
                    // 清理保存的路径，避免重复使用
                    localStorage.removeItem('MY_GPT_PATH_NAME');
                    login(savedPath);
                  } else {
                    login(window.location.origin);
                  }
                }}
              >
                {t('signin_now')}
              </Button>
            </Stack>
          ) : (
            <Group>
              <Text size={'xl'} ta="center" className="whitespace-pre-line">
                {isEmailVerify ? t('errors.email_verify') : text || 'loading'}
              </Text>
              {!text && <Loader variant="dots" />}
            </Group>
          )}
        </Center>
        {isEmailVerify && (
          <Group justify="center" mt={30} gap={'xl'}>
            <Button
              variant="outline"
              onClick={() => {
                login();
              }}
            >
              {t('signin')}
            </Button>
            <Button
              disabled={isEmailVerification}
              variant="gradient"
              gradient={{ from: '#6d28d9', to: '#be81ff' }}
              onClick={open}
            >
              <span>{t('send_again')}</span>
              {isEmailVerification && <span className="ml-2">{emailVerificationSeconds}</span>}
            </Button>
          </Group>
        )}
      </Container>

      <MailVerificationModal
        opened={opened}
        onClose={close}
        onConfirm={() => {
          emailVerificationInterval.start();
        }}
      />
    </>
  );
};
